import { Route } from "@faire/web-api/Route";
import { props as pointOfSaleProps } from "@faire/web-api/routes/point-of-sale";
import { props as posArchivedItemsProps } from "@faire/web-api/routes/point-of-sale/archived";
import { props as posConnectCloverAppProps } from "@faire/web-api/routes/point-of-sale/connect-clover-app";
import { props as posConnectShopifyAppProps } from "@faire/web-api/routes/point-of-sale/connect-shopify-app";
import { props as posLinkedItemsProps } from "@faire/web-api/routes/point-of-sale/linked";
import { props as posSuggestedMatchesProps } from "@faire/web-api/routes/point-of-sale/matches";
import { props as posOnboardingProps } from "@faire/web-api/routes/point-of-sale/onboarding";
import { props as posOrderManagerProps } from "@faire/web-api/routes/point-of-sale/order-manager/orderToken";
import { props as posOrderDetailsProps } from "@faire/web-api/routes/point-of-sale/orders/brandOrderToken";
import { props as posSettingsProps } from "@faire/web-api/routes/point-of-sale/settings";
import { props as posUnlinkedItemsProps } from "@faire/web-api/routes/point-of-sale/unlinked";
import { PageLoader } from "@faire/web/ui/CircleSpinner";
import { Switch } from "@faire/web/ui/routing/Switch";
import React, { Suspense } from "react";

import { SummaryWall } from "@faire/retailer/features/PointOfSale/components/SummaryWall";

import { LazyArchivedItems } from "./archived/LazyArchivedItems";
import { LazyPOSConnectCloverApp } from "./connect-clover-app/LazyPOSConnectCloverApp";
import { LazyPOSConnectShopifyApp } from "./connect-shopify-app/LazyPOSConnectShopifyApp";
import { LazyPOSHome } from "./landing-page/LazyPOSHome";
import { LazyLinkedItems } from "./linked/LazyLinkedItems";
import { LazySuggestedMatches } from "./matches/LazySuggestedMatches";
import { LazyPOSOnboardingPages } from "./onboarding/LazyPOSOnboardingPages";
import { LazyOrderManager } from "./order-manager/LazyOrderManager";
import { LazyOrderDetails } from "./orders/brandOrderToken/LazyOrderDetails";
import { LazyPOSSettings } from "./settings/LazyPOSSettings";
import { LazyUnlinkedItems } from "./unlinked/LazyUnlinkedItems";

export const POSRouter = ({
  orderManager,
  onboarding,
}: {
  orderManager?: boolean;
  onboarding?: boolean;
}) => {
  // Order manager and onboarding must be rendered exclusively, as they have different header contents,
  // and will cause flickering if rendered together in NextJS on page navigation.
  if (orderManager) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Route
          {...posOrderManagerProps}
          render={() => (
            <SummaryWall linkedOnly>
              <LazyOrderManager />
            </SummaryWall>
          )}
        />
      </Suspense>
    );
  }

  if (onboarding) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Route
          {...posOnboardingProps}
          render={() => (
            <SummaryWall>
              <LazyPOSOnboardingPages />
            </SummaryWall>
          )}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route
          {...posSettingsProps}
          render={() => (
            <SummaryWall linkedOnly>
              <LazyPOSSettings />
            </SummaryWall>
          )}
        />
        <Route
          {...posOrderDetailsProps}
          render={() => (
            <SummaryWall linkedOnly allowNotifications>
              <LazyOrderDetails />
            </SummaryWall>
          )}
        />
        <Route
          {...posSuggestedMatchesProps}
          render={() => (
            <SummaryWall linkedOnly allowNotifications>
              <LazySuggestedMatches />
            </SummaryWall>
          )}
        />
        <Route
          {...posLinkedItemsProps}
          render={() => (
            <SummaryWall linkedOnly allowNotifications>
              <LazyLinkedItems />
            </SummaryWall>
          )}
        />
        <Route
          {...posUnlinkedItemsProps}
          render={() => (
            <SummaryWall linkedOnly allowNotifications>
              <LazyUnlinkedItems />
            </SummaryWall>
          )}
        />
        <Route
          {...posArchivedItemsProps}
          render={() => (
            <SummaryWall linkedOnly allowNotifications>
              <LazyArchivedItems />
            </SummaryWall>
          )}
        />
        <Route
          {...posConnectCloverAppProps}
          render={() => (
            <SummaryWall>
              <LazyPOSConnectCloverApp />
            </SummaryWall>
          )}
        />
        <Route
          {...posConnectShopifyAppProps}
          render={() => (
            <SummaryWall>
              <LazyPOSConnectShopifyApp />
            </SummaryWall>
          )}
        />
        <Route
          {...pointOfSaleProps}
          exact
          render={() => (
            <SummaryWall allowNotifications>
              <LazyPOSHome />
            </SummaryWall>
          )}
        />
      </Switch>
    </Suspense>
  );
};
